import { API } from '..';
import { ITopDonator, ITopFollower, LeaderboardData } from '../../models/leaderboard.model';
import { LocalStorageService } from '../../configuration';

export interface TopInteractionItem {
  userId: string;
  userName: string | null;
  fullName: string | null;
  score: number;
  likes: number;
  comments: number;
  shares: number;
  avatar?: string | null;
}

export const fetchLeaderboardApi = () => {
  return new Promise<LeaderboardData>((resolve, reject) => {
    API.get<LeaderboardData>(`/v1/user-service/users/leaderboard`)
      .then((res) => {
        resolve(res.data);
      })
      .catch(reject);
  });
};
/**
 * Bảng xếp hạng người donate của 1 creator.
 *
 * Trước đây gọi post-service/donatepost/statistical-donate -> đọc bảng PostDonate,
 * bảng này đã NGỪNG được ghi từ khi donate chuyển sang payment-service, nên số liệu
 * đóng băng (hiện sai người donate). Nay đọc từ payment-service (CabTransactions).
 *
 * Avatar không nằm trong payment-service (ảnh ở Cassandra bên user-service) nên ghép
 * thêm bằng 1 lời gọi batch. Ghép avatar lỗi thì vẫn trả bảng xếp hạng (mất ảnh thôi).
 */
export const fetchLeaderboardUserApi = async (
  userId?: any
): Promise<LeaderboardData> => {
  const empty = { topMoney: [], topAction: [] } as unknown as LeaderboardData;
  if (!userId) return empty;

  const res = await API.get<LeaderboardData>(
    '/v1/payment-service/public/creator-top-donators',
    { params: { userId } }
  );
  const data: any = res.data || empty;
  const rows: any[] = [...(data.topMoney || []), ...(data.topAction || [])];
  const ids = Array.from(new Set(rows.map((x) => x?.userId).filter(Boolean)));
  if (ids.length === 0) return data;

  try {
    const users = await API.post<any[]>('/v1/user-service/Users/ids', ids);
    const avatarOf = new Map<string, string>(
      (users.data || []).map((u: any) => [u.userId, u.avatar])
    );
    rows.forEach((row) => {
      row.avatar = avatarOf.get(row.userId) || '';
    });
  } catch {
    // Không lấy được ảnh -> vẫn hiện bảng xếp hạng, Avatar tự fallback chữ cái đầu.
  }
  return data;
};


export const fetchTopDonatorApi = ({
  startTime,
  endTime,
  pageSize = 10,
  pageNumber = 1,
}: {
  startTime?: string;
  endTime?: string;
  pageSize?: number;
  pageNumber?: number;
} = {}) => {
  const params = new URLSearchParams();

  if (startTime) params.append("startTime", startTime);
  if (endTime) params.append("endTime", endTime);
  params.append("pageSize", pageSize.toString());
  params.append("pageNumber", pageNumber.toString());

  return new Promise<ITopDonator>((resolve, reject) => {
    API.get<ITopDonator>(`/v1/payment-service/public/top-donators?${params.toString()}`)
      .then((res) => resolve(res.data))
      .catch(reject);
  });
};


export const fetchTopFollowerApi = ({
  startTime,
  endTime,
  pageSize = 10,
  pageNumber = 1,
}: {
  startTime?: string;
  endTime?: string;
  pageSize?: number;
  pageNumber?: number;
} = {}) => {
  const params = new URLSearchParams();

  if (startTime) params.append("startTime", startTime);
  if (endTime) params.append("endTime", endTime);
  params.append("pageSize", pageSize.toString());
  params.append("pageNumber", pageNumber.toString());

  return new Promise<ITopFollower>((resolve, reject) => {
    API.get<ITopFollower>(`/v1/user-service/public/top-followed-users?${params.toString()}`)
      .then((res) => resolve(res.data))
      .catch(reject);
  });
};


/**
 * Bảng xếp hạng tương tác (thích/bình luận/chia sẻ) - đọc từ post-service.
 *
 * post-service đã trả sẵn avatar. Chỉ khi vẫn còn dòng thiếu avatar mới ghép thêm bằng 1 lời
 * gọi batch (theo cách fetchLeaderboardUserApi đang làm) - và chỉ khi khách đã đăng nhập, vì
 * trang /vinh-danh + khung trang chủ đều xem được lúc chưa đăng nhập, mà endpoint batch cần
 * token nên khách gọi sẽ dính 401 + refresh-token thất bại ở mỗi lần xem trang.
 * Ghép avatar lỗi thì vẫn trả danh sách (mất ảnh thôi).
 */
export const fetchTopInteractionsApi = async ({
  startTime,
  endTime,
  take,
}: {
  startTime?: string;
  endTime?: string;
  take?: number;
} = {}): Promise<TopInteractionItem[]> => {
  const params = new URLSearchParams();

  if (startTime) params.append('startTime', startTime);
  if (endTime) params.append('endTime', endTime);
  if (take !== undefined) params.append('take', take.toString());

  const res = await API.get<TopInteractionItem[]>(
    `/v1/post-service/public/top-interactions?${params.toString()}`
  );
  const rows: TopInteractionItem[] = res.data || [];

  const isLoggedIn = Boolean(LocalStorageService.getItem('accessToken'));
  const missingAvatarIds = Array.from(
    new Set(rows.filter((x) => !x?.avatar && x?.userId).map((x) => x.userId))
  );
  if (!isLoggedIn || missingAvatarIds.length === 0) return rows;

  try {
    const users = await API.post<any[]>('/v1/user-service/Users/ids', missingAvatarIds);
    const avatarOf = new Map<string, string>(
      (users.data || []).map((u: any) => [u.userId, u.avatar])
    );
    rows.forEach((row) => {
      row.avatar = avatarOf.get(row.userId) || row.avatar || '';
    });
  } catch {
    // Không lấy được ảnh -> vẫn trả bảng xếp hạng, Avatar tự fallback chữ cái đầu.
  }
  return rows;
};

export const fetchTopReceiversApi = ({
  startTime,
  endTime,
  pageSize = 10,
  pageNumber = 1,
}: {
  startTime?: string;
  endTime?: string;
  pageSize?: number;
  pageNumber?: number;
} = {}) => {
  const params = new URLSearchParams();

  if (startTime) params.append("startTime", startTime);
  if (endTime) params.append("endTime", endTime);
  params.append("pageSize", pageSize.toString());
  params.append("pageNumber", pageNumber.toString());

  return new Promise<ITopDonator>((resolve, reject) => {
    API.get<ITopDonator>(`/v1/payment-service/public/top-receivers?${params.toString()}`)
      .then((res) => resolve(res.data))
      .catch(reject);
  });
};

